import React, { useEffect, useState, useCallback } from "react";
import { Box, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import { environments } from "constants/environments";
import { useAuth } from "contexts/auth";
import { callApi } from "apiServices/callApi";
import { IApiMetaData } from "interfaces/apiMetaData";
import SkeletonSubCategory from "shared/SkeletonSubCategory";
import CardItem from "components/card/CardItem";
import { IFavoriteItem } from "interfaces/favoriteItem ";

const Favorites: React.FC = () => {
  const [data, setData] = useState<IFavoriteItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const { sharedData } = useAuth();
  const itemsAPI = environments.domain + sharedData?.apisList.Item.GetByIds;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: IApiMetaData = await callApi(
        "POST",
        itemsAPI,
        { ids: sharedData?.user?.favorites },
        {},
        {
          select: "title supTitle cover keywords",
        }
      );

      if (response.Status === 200) {
        setData(response.Data.Content);
      } else {
        setIsError(true);
      }
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [itemsAPI, sharedData?.user?.favorites]);

  useEffect(() => {
    if (
      !sharedData?.user?.favorites ||
      sharedData.user.favorites.length === 0
    ) {
      setIsLoading(false);
      return;
    }
    fetchData();
  }, [fetchData, sharedData?.user?.favorites]);

  if (isError) {
    return (
      <Text fontSize="xl" fontWeight="600" color="red.500">
        Failed to load your favorites. Please try again later.
      </Text>
    );
  }

  if (isLoading) {
    return <SkeletonSubCategory />;
  }

  const convertPathImage = (path: string) => {
    if (!path) return "";
    const removeOldDomain = String(path).replace(
      "http://datalib.cardiffinstitute.uk/",
      ""
    );
    return `${environments.domain}/uploads/${removeOldDomain}`;
  };

  return (
    <Box pt={{ base: "180px", md: "80px", xl: "80px" }}>
      <Flex
        flexDirection="column"
        gridArea={{ xl: "1 / 1 / 2 / 3", "2xl": "1 / 1 / 2 / 2" }}
      >
        <Flex direction="column">
          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 2, xl: 3 }}
            gap="20px"
            mb={{ base: "20px", xl: "0px" }}
          >
            {data?.length === 0 ? (
              <Text fontSize="xl" fontWeight="600">
              Your favorites list is empty
              </Text>
            ) : (
              data.map((item: IFavoriteItem) => (
              <CardItem
                id={item._id}
                key={item._id}
                name={item.title}
                author={item.supTitle || "Unknown Author"}
                image={convertPathImage(item.cover || '') || "placeholder.jpg"}
                currentbid={
                item.keywords
                  ?.filter((key: any) => key !== undefined && key !== "undefined")
                  .map((key: any) => `#${key[0]?.toUpperCase() + key?.slice(1)}`)
                  .join(" ") || ""
                }
              />
              ))
            )}
          </SimpleGrid>
        </Flex>
      </Flex>
    </Box>
  );
};

export default Favorites;
